import { Capacitor } from '@capacitor/core';

type BackHandler = {
  id: string;
  priority: number;
  handler: () => boolean; // return true to consume the event
};

const handlers: BackHandler[] = [];
let listenerBound = false;

function sortHandlers() {
  handlers.sort((a, b) => b.priority - a.priority); // highest priority first
}

async function ensureListener() {
  if (listenerBound || !Capacitor.isNativePlatform()) return;
  listenerBound = true;

  const { App } = await import('@capacitor/app');
  await App.addListener('backButton', () => {
    // Iterate highest-priority first; first handler returning true consumes the event
    for (const entry of handlers) {
      if (entry.handler()) return;
    }
    // No handler consumed — do nothing (let web popstate handle it if needed)
  });
}

/**
 * Register a back-button handler with a given priority.
 * Higher priority = gets the event first.
 * Handler should return `true` to consume the event, `false` to pass it along.
 *
 * @returns unregister function
 */
export function registerBackHandler(
  id: string,
  priority: number,
  handler: () => boolean,
): () => void {
  // Remove any existing handler with the same id
  const idx = handlers.findIndex((h) => h.id === id);
  if (idx !== -1) handlers.splice(idx, 1);

  handlers.push({ id, priority, handler });
  sortHandlers();

  void ensureListener();

  return () => {
    const i = handlers.findIndex((h) => h.id === id);
    if (i !== -1) handlers.splice(i, 1);
  };
}
